// ─────────────────────────────────────────────────────────────────────────
// AV Simulation Tool — Project Package (.avproj) format
//
// Single-file packaged project for export / share / import.
// Container format: ZIP. Contents:
//   /manifest.json         — schema version, app version, stats, integrity
//   /scene.json            — scene objects, projectors, settings, measurements
//   /assets/<sha1>.<ext>   — every referenced asset (textures, content, models)
//   /preview.jpg           — auto-captured thumbnail (optional)
//
// On export:
//   - Every URL pointing at /api/files/serve/* or /api/content/serve/* is
//     fetched, hashed (sha-1 of bytes), and stored once under that hash.
//     Multiple objects pointing at the same URL get deduped automatically.
//   - The scene's URL fields are rewritten to "assets/<hash>.<ext>".
//   - Missing assets (HTTP errors) are flagged in manifest.missingAssets,
//     export continues so the user gets a partial package they can fix.
//
// On import:
//   - manifest.json is parsed first (version check).
//   - Each asset in /assets/ is re-uploaded to the importing user's storage
//     using the appropriate endpoint (models → /files/upload, media →
//     /content/upload). The new server-issued URL replaces the relative
//     "assets/<hash>.<ext>" reference in the scene.
//   - A new project is created (always — never replaces current).
//   - Object IDs and all transforms/configs are preserved verbatim.
// ─────────────────────────────────────────────────────────────────────────
import JSZip from 'jszip';
import { filesApi, contentApi, projectsApi } from './api';

export const PACKAGE_VERSION = '1.0';
export const APP_NAME = 'AV Simulation Tool';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MODEL_EXTS = ['obj', 'fbx', 'gltf', 'glb', 'mtl'];

// Field paths inside the project that may contain asset URLs (or relative
// "/api/files|content/serve/..." paths). Walked by both export and import.
const ASSET_FIELD_WALKERS = {
  // sceneObjects[*].textureUrl
  sceneObjectTexture: (obj) => obj?.textureUrl,
  // sceneObjects[*].file_path  (mesh type — 3D model)
  sceneObjectFile: (obj) => obj?.file_path,
  // sceneObjects[*].ledConfig.contentUrl
  ledContent: (obj) => obj?.ledConfig?.contentUrl,
  // projectors[*].projector_image
  projectorImage: (proj) => proj?.projector_image,
  // worldSettings.testPatternLogo
  worldLogo: (ws) => ws?.testPatternLogo,
};

// ════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════

function isPackagedRef(url) {
  if (typeof url !== 'string' || !url) return false;
  return url.startsWith('assets/');
}

function isFetchableAssetUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  if (url.startsWith('data:')) return false; // base64 stays inline
  if (url.startsWith('blob:')) return false; // transient, skip
  if (url.startsWith('assets/')) return false; // already packaged
  return true;
}

function resolveUrl(url) {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // absolute URL from another domain → extract API path
  const m = url.match(/(\/api\/(?:content|files)\/serve\/.+)/);
  if (m) return `${BACKEND_URL}${m[1]}`;
  return url;
}

function extFromUrl(url, fallbackContentType) {
  // Try to pull extension from URL path
  try {
    const u = new URL(resolveUrl(url));
    const path = u.pathname;
    const m = path.match(/\.([a-zA-Z0-9]{2,5})(?:$|\?)/);
    if (m) return m[1].toLowerCase();
  } catch (e) {
    // not a valid URL — fall through
  }
  if (fallbackContentType) {
    const map = {
      'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png',
      'image/webp': 'webp', 'image/gif': 'gif', 'video/mp4': 'mp4',
      'video/webm': 'webm', 'model/gltf-binary': 'glb', 'model/obj': 'obj',
    };
    if (map[fallbackContentType.toLowerCase()]) return map[fallbackContentType.toLowerCase()];
  }
  return 'bin';
}

async function sha1Hex(arrayBuffer) {
  const hashBuf = await crypto.subtle.digest('SHA-1', arrayBuffer);
  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Walks every asset-bearing field in the project state and returns a Map
// keyed by the original URL. Each entry: { sources: [{path,setter,obj}], url }.
function collectAssetUrls(state) {
  const map = new Map(); // url → { sources: [{ kind, idx, fieldKey }] }

  const remember = (url, kind, idx, fieldKey) => {
    if (!isFetchableAssetUrl(url)) return;
    if (!map.has(url)) map.set(url, { sources: [] });
    map.get(url).sources.push({ kind, idx, fieldKey });
  };

  (state.sceneObjects || []).forEach((obj, idx) => {
    remember(ASSET_FIELD_WALKERS.sceneObjectTexture(obj), 'sceneObject', idx, 'textureUrl');
    remember(ASSET_FIELD_WALKERS.sceneObjectFile(obj), 'sceneObject', idx, 'file_path');
    remember(ASSET_FIELD_WALKERS.ledContent(obj), 'sceneObject', idx, 'ledConfig.contentUrl');
  });
  (state.projectors || []).forEach((p, idx) => {
    remember(ASSET_FIELD_WALKERS.projectorImage(p), 'projector', idx, 'projector_image');
  });
  remember(ASSET_FIELD_WALKERS.worldLogo(state.worldSettings || {}), 'world', 0, 'testPatternLogo');

  return map;
}

function rewriteAssetUrls(state, urlMap /* original→{newRef}  */) {
  const setField = (target, dottedKey, value) => {
    const parts = dottedKey.split('.');
    let t = target;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!t[parts[i]] || typeof t[parts[i]] !== 'object') t[parts[i]] = {};
      t = t[parts[i]];
    }
    t[parts[parts.length - 1]] = value;
  };
  const remap = (orig) => urlMap.get(orig)?.newRef ?? orig;

  state.sceneObjects = (state.sceneObjects || []).map(obj => {
    const out = { ...obj };
    if (out.textureUrl && urlMap.has(out.textureUrl)) out.textureUrl = remap(out.textureUrl);
    if (out.file_path && urlMap.has(out.file_path)) out.file_path = remap(out.file_path);
    if (out.ledConfig?.contentUrl && urlMap.has(out.ledConfig.contentUrl)) {
      out.ledConfig = { ...out.ledConfig, contentUrl: remap(out.ledConfig.contentUrl) };
    }
    return out;
  });
  state.projectors = (state.projectors || []).map(p => {
    const out = { ...p };
    if (out.projector_image && urlMap.has(out.projector_image)) out.projector_image = remap(out.projector_image);
    return out;
  });
  if (state.worldSettings?.testPatternLogo && urlMap.has(state.worldSettings.testPatternLogo)) {
    state.worldSettings = { ...state.worldSettings, testPatternLogo: remap(state.worldSettings.testPatternLogo) };
  }
  return state;
}

// ════════════════════════════════════════════════════════════════════════
// EXPORT
// ════════════════════════════════════════════════════════════════════════

/**
 * @param {object} state - { sceneObjects, projectors, worldSettings, projectSettings, measurements, savedCameras, projectName }
 * @param {object} opts  - { previewDataUrl?: string, onProgress?: (pct, label) => void }
 * @returns {Promise<{ blob: Blob, fileName: string, stats: object }>}
 */
export async function exportProject(state, opts = {}) {
  const onProgress = opts.onProgress || (() => {});
  onProgress(0, 'Collecting assets…');

  const zip = new JSZip();
  const assetsFolder = zip.folder('assets');

  // 1. Collect every fetchable asset URL.
  const urlMap = collectAssetUrls(state);
  const totalUrls = urlMap.size;
  const missing = [];
  let assetBytes = 0;
  let processed = 0;

  // 2. Fetch + hash + add to zip; remember new relative ref.
  for (const [origUrl, info] of urlMap.entries()) {
    try {
      const fetchUrl = resolveUrl(origUrl);
      const res = await fetch(fetchUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buf = await res.arrayBuffer();
      const ct = res.headers.get('content-type') || '';
      const ext = extFromUrl(origUrl, ct);
      const hash = await sha1Hex(buf);
      const relPath = `${hash}.${ext}`;
      // Only add once even if hash repeats (dedup)
      if (!assetsFolder.file(relPath)) {
        assetsFolder.file(relPath, buf);
        assetBytes += buf.byteLength;
      }
      info.newRef = `assets/${relPath}`;
    } catch (err) {
      // Graceful: keep original URL and flag in manifest
      missing.push({ url: origUrl, sources: info.sources, error: err.message });
      info.newRef = origUrl;
    }
    processed += 1;
    onProgress(Math.round((processed / Math.max(1, totalUrls)) * 70), `Bundling assets ${processed}/${totalUrls}…`);
  }

  // 3. Build scene.json with rewritten URLs.
  onProgress(75, 'Writing scene…');
  const sceneClone = JSON.parse(JSON.stringify({
    sceneObjects: state.sceneObjects || [],
    projectors: state.projectors || [],
    worldSettings: state.worldSettings || {},
    projectSettings: state.projectSettings || {},
    measurements: state.measurements || [],
    savedCameras: state.savedCameras || [],
  }));
  rewriteAssetUrls(sceneClone, urlMap);
  const sceneJsonStr = JSON.stringify(sceneClone, null, 2);
  zip.file('scene.json', sceneJsonStr);

  // Integrity hash of scene.json
  const sceneBytes = new TextEncoder().encode(sceneJsonStr);
  const sceneHash = await sha1Hex(sceneBytes.buffer);

  // 4. Optional preview thumbnail.
  if (opts.previewDataUrl && typeof opts.previewDataUrl === 'string' && opts.previewDataUrl.startsWith('data:image')) {
    try {
      const base64 = opts.previewDataUrl.split(',')[1];
      const bin = atob(base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      zip.file('preview.jpg', arr);
    } catch (e) { /* skip preview on failure */ }
  }

  // 5. Manifest.
  const manifest = {
    format: 'avproj',
    version: PACKAGE_VERSION,
    appName: APP_NAME,
    exportedAt: new Date().toISOString(),
    projectName: state.projectName || state.projectSettings?.name || 'Untitled',
    stats: {
      sceneObjects: (state.sceneObjects || []).length,
      projectors: (state.projectors || []).length,
      assets: totalUrls - missing.length,
      assetBytes,
      missingAssets: missing.length,
    },
    sceneSha1: sceneHash,
    missingAssets: missing.map(m => ({ url: m.url, error: m.error })),
  };
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  // 6. Generate blob.
  onProgress(85, 'Compressing…');
  const blob = await zip.generateAsync(
    { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
    (meta) => onProgress(85 + Math.round(meta.percent * 0.15), 'Compressing…')
  );

  const safeName = (manifest.projectName || 'project').replace(/[^\w\-. ]+/g, '_').slice(0, 60).trim() || 'project';
  const ts = new Date().toISOString().slice(0, 10);
  const fileName = `${safeName}-${ts}.avproj`;

  onProgress(100, 'Done.');
  return { blob, fileName, stats: manifest.stats, manifest };
}

// ════════════════════════════════════════════════════════════════════════
// IMPORT — preview pass (no side effects)
// ════════════════════════════════════════════════════════════════════════

/**
 * Reads .avproj and returns metadata + scene preview without uploading.
 * Caller can show the data in a confirmation modal before committing.
 */
export async function inspectPackage(file) {
  const zip = await JSZip.loadAsync(file);
  const manifestRaw = zip.file('manifest.json');
  const sceneRaw = zip.file('scene.json');
  if (!manifestRaw || !sceneRaw) {
    throw new Error('Invalid .avproj — missing manifest.json or scene.json');
  }
  const manifest = JSON.parse(await manifestRaw.async('string'));
  const scene = JSON.parse(await sceneRaw.async('string'));

  if (manifest.format !== 'avproj') {
    throw new Error(`Unsupported package format: ${manifest.format}`);
  }
  if (!manifest.version) {
    throw new Error('Package version missing.');
  }
  // Forward-compat: accept any major version equal to ours; warn for newer minors
  const [pkgMajor] = String(manifest.version).split('.').map(n => parseInt(n) || 0);
  const [appMajor] = PACKAGE_VERSION.split('.').map(n => parseInt(n) || 0);
  if (pkgMajor > appMajor) {
    throw new Error(`Package version ${manifest.version} is newer than this app supports (${PACKAGE_VERSION}). Please update.`);
  }

  // Read preview if present
  let previewUrl = null;
  const previewFile = zip.file('preview.jpg');
  if (previewFile) {
    const blob = await previewFile.async('blob');
    previewUrl = URL.createObjectURL(blob);
  }

  // Count assets present
  const assetFiles = Object.keys(zip.files).filter(p => p.startsWith('assets/') && !zip.files[p].dir);

  return {
    zip, // pass to commitImport later
    manifest,
    scene,
    previewUrl,
    assetCount: assetFiles.length,
  };
}

// ════════════════════════════════════════════════════════════════════════
// IMPORT — commit (uploads assets, creates new project, returns it)
// ════════════════════════════════════════════════════════════════════════

/**
 * Re-uploads all bundled assets to the user's storage, rewrites URL refs in
 * the scene, creates a new project record, and returns the loaded project
 * payload ready for loadProject(). Always creates a NEW project (never
 * replaces current).
 *
 * @param {object} pkg - object returned by inspectPackage()
 * @param {object} opts - { onProgress?: (pct, label) => void, projectNameOverride?: string }
 */
export async function commitImport(pkg, opts = {}) {
  const onProgress = opts.onProgress || (() => {});
  const { zip, manifest, scene } = pkg;

  // 1. Upload each /assets/ entry; build oldRef → newUrl map.
  const assetEntries = Object.keys(zip.files)
    .filter(p => p.startsWith('assets/') && !zip.files[p].dir);
  const total = assetEntries.length;
  const remap = new Map(); // "assets/<hash>.<ext>" → "/api/files/serve/<id>" or "/api/content/serve/<id>"
  let processed = 0;

  for (const path of assetEntries) {
    const blob = await zip.files[path].async('blob');
    const ext = (path.split('.').pop() || '').toLowerCase();
    const isModel = MODEL_EXTS.includes(ext);
    const filename = path.replace(/^assets\//, '');
    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });

    try {
      let resp;
      if (isModel) {
        resp = await filesApi.upload(file);
      } else {
        resp = await contentApi.upload(file);
      }
      const data = resp.data || resp; // axios style
      const newId = data.id;
      if (!newId) throw new Error('Upload returned no id');
      const newUrl = isModel
        ? `/api/files/serve/${newId}`
        : `/api/content/serve/${newId}`;
      remap.set(path, newUrl);
    } catch (err) {
      // Fall back: keep relative ref (will render as broken). Logged for user.
      // eslint-disable-next-line no-console
      console.warn('[avproj] failed to upload', path, err);
      remap.set(path, path);
    }
    processed += 1;
    onProgress(Math.round((processed / Math.max(1, total)) * 80), `Uploading assets ${processed}/${total}…`);
  }

  // 2. Rewrite scene URL refs from "assets/<hash>.<ext>" → new server URLs.
  const rewriteRef = (val) => (typeof val === 'string' && remap.has(val)) ? remap.get(val) : val;
  const newScene = {
    ...scene,
    sceneObjects: (scene.sceneObjects || []).map(obj => {
      const out = { ...obj };
      if (isPackagedRef(out.textureUrl)) out.textureUrl = rewriteRef(out.textureUrl);
      if (isPackagedRef(out.file_path)) out.file_path = rewriteRef(out.file_path);
      if (out.ledConfig?.contentUrl && isPackagedRef(out.ledConfig.contentUrl)) {
        out.ledConfig = { ...out.ledConfig, contentUrl: rewriteRef(out.ledConfig.contentUrl) };
      }
      return out;
    }),
    projectors: (scene.projectors || []).map(p => {
      const out = { ...p };
      if (isPackagedRef(out.projector_image)) out.projector_image = rewriteRef(out.projector_image);
      return out;
    }),
    worldSettings: (() => {
      const ws = { ...(scene.worldSettings || {}) };
      if (isPackagedRef(ws.testPatternLogo)) ws.testPatternLogo = rewriteRef(ws.testPatternLogo);
      return ws;
    })(),
  };

  // 3. Create new project + populate.
  onProgress(85, 'Creating project…');
  const projectName = opts.projectNameOverride
    || `${manifest.projectName || 'Imported Project'} (Imported)`;
  const unit = newScene.projectSettings?.unit || 'm';
  const createRes = await projectsApi.create({ name: projectName, unit });
  const newProject = createRes.data || createRes;

  await projectsApi.update(newProject.id, {
    scene_objects: newScene.sceneObjects,
    projectors: newScene.projectors,
    unit,
    measurements: newScene.measurements || [],
    saved_cameras: newScene.savedCameras || [],
    world_settings: newScene.worldSettings || {},
  });

  onProgress(100, 'Done.');

  return {
    project: {
      ...newProject,
      name: projectName,
      unit,
      scene_objects: newScene.sceneObjects,
      projectors: newScene.projectors,
      world_settings: newScene.worldSettings,
      measurements: newScene.measurements || [],
      saved_cameras: newScene.savedCameras || [],
    },
    manifest,
  };
}
